import {AppLocalStorage, AppSessionStorage} from './lib/app-localstorage';
import {PortfolioPeriodEnum} from '../api-client-kt/src';

export type ApiEnvironment = 'local' | 'etractive' | 'dev' | 'preprod' | 'prod' | null;
export type ApiServiceType = 'kt' | 'py';

export class AppConstants {
    static readonly NULL_UUID = '00000000-0000-0000-0000-000000000000';

    static readonly APP_LOCAL_STORAGE_KEY = 'pfm.app';
    static readonly APP_USER_LOCALE_KEY = 'pfm.user.locale';

    static readonly APP_PORTFOLIO_ARCHIVE_SELECTED_UWY = 'pfm.selected.portfolio.archive.uwy';

    static readonly DEVTOOLS_LOCAL_STORAGE_KEY_PREFIX = 'devtools';
    static readonly DEVTOOLS_ENABLED_LOCAL_STORAGE_KEY = 'devtools.enabled';
    static readonly DEVTOOLS_API_ENV_KT_KEY = 'devtools.api.env.kt';
    static readonly DEVTOOLS_API_ENV_PY_KEY = 'devtools.api.env.py';
    static readonly DEVTOOLS_CHANGE_DETECTION_FREQ_KEY = 'devtools.change.detection.frequency';
    static readonly DEVTOOLS_GZIP_REQUESTS_KEY = 'devtools.gzip.requests';
    static readonly DEVTOOLS_CUSTOM_PERMISSIONS_KEY = 'devtools.api.custompermissions';
    static readonly DEVTOOLS_API_USER_ROLE_KEY = 'devtools.api.userrole';

    static readonly STORAGE_PREFIX = 'wjgrid.layouts.';
    static readonly STORAGE_TREE_KEY = 'wjgrid.layouts.tree';

    static readonly DEVTOOLS_KEY_PREFIX = [AppConstants.APP_LOCAL_STORAGE_KEY, AppConstants.DEVTOOLS_LOCAL_STORAGE_KEY_PREFIX].join('.');

    static readonly PYFORMULA_SEGMENT_ID_NAME = 'segmentId';
    static readonly PYFORMULA_NEW_BUSINESS_GNWP_EUR_TARGET = 'pfm_new_business_gnwp_target';
    static readonly PYFORMULA_NEW_BUSINESS_NLR_TARGET = 'pfm_new_business_nlr_target';
    static readonly PYFORMULA_NEW_BUSINESS_GNWP_EUR = 'pfm_new_business_gnwp';
    static readonly PYFORMULA_NEW_BUSINESS_NLR = 'pfm_new_business_nlr';
    static readonly PYFORMULA_NEW_BUSINESS_GNWP_EUR_RENWED_UWY = 'pfm_new_business_gnwp_renewed_uwy';
    static readonly PYFORMULA_NEW_BUSINESS_GNWP_EUR_NEW_TOTAL_TARGETS = 'pfm_new_business_gnwp_new_total_targets';

    static readonly PYFORMULA_ACCOUNTS_TABLE_NAME = 'accounts';
    static readonly PYFORMULA_ACCOUNT_CHECKS_TABLE_NAME = 'account_checks_results';
    static readonly PYFORMULA_ACCOUNT_CHECKS_DICTIONARY_TABLE_NAME = 'account_checks_dictionary';
    static readonly PYFORMULA_TIERS_TABLE_NAME = 'tiers';
    static readonly PYFORMULA_TRC_TABLE_NAME = 'target_rate_change';
    static readonly PYFORMULA_TRC_INTERPOLATION_TABLE_NAME = 'target_rate_change_interpolation';
    static readonly PYFORMULA_PORTFOLIO_SUMMARY_TABLE_NAME = 'portfolio_summary';
    static readonly PYFORMULA_TIERING_STATS_TABLE_NAME = 'tiering_stats';
    static readonly PYFORMULA_TIERING_BE_TABLE_NAME = 'tiering_be';

    static readonly PYFORMULA_ACCOUNT_DATA_METHOD = 'pfm_account_data';
    static readonly PYFORMULA_ACCOUNT_DATA_CHECKS_METHOD = 'pfm_account_checks';

    static readonly VIEWSETTINGS_ACCOUNTGRID_HEADER_BINDING = 'accountGrid.headerBinding';
    static readonly VIEWSETTINGS_ACCOUNTGRID_COMMENTPANE_VISIBLE = 'accountGrid.commentPaneVisible';
    static readonly VIEWSETTINGS_RATECHANGE_CALIBRATION_VISIBLE = 'rateChangeCalibration.visible';


    // Upper and lower date bounds for expiry date testing in the periods
    // using strict 'lower than' and 'bigger than' operators: lower < date < upper
    // ('strict' is necessary because we want to use it in Wijmo grid filters
    // that do not support <= and >= for date columns
    static ThresholdDates = {
        [PortfolioPeriodEnum.Hy]: { lower: { month: 0, day:  1 }, upper: { month: 6, day: 1 } },
        [PortfolioPeriodEnum.Q1]: { lower: { month: 0, day:  1 }, upper: { month: 3, day: 1 } },
        [PortfolioPeriodEnum.Q2]: { lower: { month: 3, day:  1 }, upper: { month: 6, day: 1 } },
        [PortfolioPeriodEnum.Q3]: { lower: { month: 6, day:  1 }, upper: { month: 9, day: 1 } },
        [PortfolioPeriodEnum.Q4]: { lower: { month: 9, day:  1 }, upper: { month: 12, day: 2 } }
    };

    static getUserLocale(): string {
        return AppLocalStorage.getItem(AppConstants.APP_USER_LOCALE_KEY) ?? 'en';
    }

    static setUserLocale(locale: string) {
        AppLocalStorage.setItem(AppConstants.APP_USER_LOCALE_KEY, locale);
    }

    static getSelectedPortfolioArchiveUwy() {
        return AppLocalStorage.getNumberItem(AppConstants.APP_PORTFOLIO_ARCHIVE_SELECTED_UWY);
    }

    static setSelectedPortfolioArchiveUwy(uwy: number | null) {
        AppLocalStorage.setNumberItem(AppConstants.APP_PORTFOLIO_ARCHIVE_SELECTED_UWY, uwy);
    }

    static getAccountGridHeaderBinding(): string | null {
        return AppSessionStorage.getItem(AppConstants.VIEWSETTINGS_ACCOUNTGRID_HEADER_BINDING);
    }

    static setAccountGridHeaderBinding(binding: string): void {
        AppSessionStorage.setItem(AppConstants.VIEWSETTINGS_ACCOUNTGRID_HEADER_BINDING, binding);
    }

    static getAccountCommentsPaneVisible(): boolean {
        return AppSessionStorage.getBooleanItem(AppConstants.VIEWSETTINGS_ACCOUNTGRID_COMMENTPANE_VISIBLE) ?? false
    }

    static setAccountCommentsPaneVisible(visible: boolean): void {
        AppSessionStorage.setItem(AppConstants.VIEWSETTINGS_ACCOUNTGRID_COMMENTPANE_VISIBLE, visible);
    }

    static getApplyRateChangeSectionVisible(): boolean {
        return AppSessionStorage.getBooleanItem(AppConstants.VIEWSETTINGS_RATECHANGE_CALIBRATION_VISIBLE) ?? false;
    }

    static setApplyRateChangeSectionVisible(visible: boolean): void {
        AppSessionStorage.setItem(AppConstants.VIEWSETTINGS_RATECHANGE_CALIBRATION_VISIBLE, visible);
    }

    static getNavBarMinimized(): boolean {
        return AppLocalStorage.getBooleanItem(AppConstants.APP_LOCAL_STORAGE_KEY + '.navbar.minimized') ?? false;
    }

    static setNavBarMinimized(minimized: boolean): void {
        AppLocalStorage.setItem(AppConstants.APP_LOCAL_STORAGE_KEY + '.navbar.minimized', minimized);
    }

    static getApiEnvKt(): ApiEnvironment {
        return AppLocalStorage.getItem(AppConstants.DEVTOOLS_API_ENV_KT_KEY) as ApiEnvironment;
    }

    static setApiEnvKt(env: ApiEnvironment): void {
        AppLocalStorage.setItem(AppConstants.DEVTOOLS_API_ENV_KT_KEY, env);
    }

    static getApiEnvPy(): ApiEnvironment {
        return AppLocalStorage.getItem(AppConstants.DEVTOOLS_API_ENV_PY_KEY) as ApiEnvironment;
    }

    static setApiEnvPy(env: ApiEnvironment): void {
        AppLocalStorage.setItem(AppConstants.DEVTOOLS_API_ENV_PY_KEY, env);
    }

    static getChangeDetectionFrequency(): number {
        return AppLocalStorage.getNumberItem(AppConstants.DEVTOOLS_CHANGE_DETECTION_FREQ_KEY) ?? 10_000;
    }

    static setChangeDetectionFrequency(frequency: number | undefined | null): void {
        AppLocalStorage.setItem(AppConstants.DEVTOOLS_CHANGE_DETECTION_FREQ_KEY, frequency);
    }

    static getGzipRequests(): boolean {
        return AppLocalStorage.getBooleanItem(AppConstants.DEVTOOLS_GZIP_REQUESTS_KEY) ?? true;
    }

    static setGzipRequests(gzip: boolean | undefined | null): void {
        AppLocalStorage.setItem(AppConstants.DEVTOOLS_GZIP_REQUESTS_KEY, gzip);
    }

    static customPermissions(): boolean {
        return AppLocalStorage.getBooleanItem(AppConstants.DEVTOOLS_CUSTOM_PERMISSIONS_KEY) ?? true;
    }

    static setUseCustomPermissions(useCustomPermissions: boolean | undefined | null): void {
        AppLocalStorage.setItem(AppConstants.DEVTOOLS_CUSTOM_PERMISSIONS_KEY, useCustomPermissions);
    }

    static readDevMode() {
        return AppLocalStorage.getBooleanItem(AppConstants.DEVTOOLS_ENABLED_LOCAL_STORAGE_KEY);
    }

    static clearDevMode() {
        for (let key in localStorage) {
            if (key.startsWith(AppConstants.DEVTOOLS_KEY_PREFIX)) {
                localStorage.removeItem(key);
            }
        }
    }
}

interface AppEnvironmentConfig {
    environment: string;
    wijmoKey: string;
    authClientId: string;
    outOfServiceRedirectUrl: string | null | undefined;
}

export const AppEnvironment: AppEnvironmentConfig = {
    environment: 'development',
    wijmoKey: '***',
    authClientId: '***',
    outOfServiceRedirectUrl: null,
};

